// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, BackHandler, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '@/components/Themed';
import { HeaderUser } from '@/components/HeaderUser';
import appColors from '@/theme/colors';
import { SIDEBAR_WIDTH_DEFAULT, SIDEBAR_WIDTH_WIDE, SIDEBAR_BREAKPOINT, MOBILE_BREAKPOINT } from '../constants';
import type { SidebarSelection, Course } from '../types';
import { useTourEditData } from '../hooks/useTourEditData';
import { useUser } from '@/app/user-context';
import { useCurrentPlayerId, useRecentCourses } from '@/hooks/useRecentCourses';
import { setCurrentTourId } from '@/services/currentTour';
import { requestBugReport } from '@/services/bugReportRequest';
import { setCurrentRoundId, setCurrentHole } from '@/services/currentRound';
import { todayLocalDate } from '@/services/localDate';
import { addOneDay, sectionTitle } from '../utils';

import Sidebar from './Sidebar';
import MobileDetailHeader from './MobileDetailHeader';
import EditorPanel from './EditorPanel';
import ConfirmDialog from './shared/ConfirmDialog';

type Props = {
  tourId: string;
  /**
   * Mobile only: open the Tour basics editor directly instead of the menu.
   * Used right after tour creation so naming the tour costs no extra tap.
   */
  startInDetail?: boolean;
};

export default function TourEditLayout({ tourId, startInDetail = false }: Props) {
  const router = useRouter();
  // This screen renders with headerShown: false, and the app is edge-to-edge on Android, so the
  // breadcrumb row is drawn underneath the status bar. Without the inset the system bar eats the
  // taps on "← Tours" and the link looks dead.
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const sidebarWidth = windowWidth >= SIDEBAR_BREAKPOINT ? SIDEBAR_WIDTH_WIDE : SIDEBAR_WIDTH_DEFAULT;
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const data = useTourEditData(tourId);

  const { user } = useUser();
  const currentPlayerId = useCurrentPlayerId(user?.id, user?.token);
  const recentCourses = useRecentCourses(currentPlayerId, user?.token);

  const isFocused = useIsFocused();

  const [selection, setSelection] = useState<SidebarSelection>({ kind: 'tour-name' });
  const [expandedRoundId, setExpandedRoundId] = useState<string | null>(null);
  const [deleteRoundConfirm, setDeleteRoundConfirm] = useState<string | null>(null);
  // Mobile-only view toggle; desktop renders the split layout and ignores it.
  const [mobileView, setMobileView] = useState<'menu' | 'detail'>(startInDetail ? 'detail' : 'menu');

  const playerCount = data.playerRows.filter((r) => r.kind === 'existing').length;
  const gameCount = data.tournamentGamesState.gameIds.length;

  // Single rounds are hidden tours and have no tour-level editing; send them to the
  // round's summary (or the tour list) instead of the tour edit screen.
  useEffect(() => {
    if (!data.isHidden) return;
    const firstRoundId = data.orderedRounds.find((round) => !!round.id)?.id;
    router.replace(firstRoundId ? `/round/${firstRoundId}/summary` : '/my-tours');
  }, [data.isHidden, data.orderedRounds, router]);

  const handleSelect = useCallback(
    (sel: SidebarSelection) => {
      setSelection(sel);
      if (isMobile) setMobileView('detail');
    },
    [isMobile]
  );

  const handleBackToMenu = useCallback(() => {
    setMobileView('menu');
  }, []);

  // Android hardware back: detail → menu first, then default (leave screen).
  useEffect(() => {
    if (!isMobile || mobileView !== 'detail') return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      setMobileView('menu');
      return true;
    });
    return () => sub.remove();
  }, [isMobile, mobileView]);

  const handleExpandRound = useCallback((roundId: string | null) => {
    setExpandedRoundId(roundId);
  }, []);

  // --- Instant round creation ---
  const handleAddRound = useCallback(() => {
    const lastRound = data.orderedRounds[data.orderedRounds.length - 1];
    // The course is always picked explicitly in Round Settings — a new round never
    // inherits (or guesses) one, so the user can't miss a wrong prefill.
    const course = null;
    const lastDate = lastRound?.playDate ?? todayLocalDate();
    const nextDate = addOneDay(lastDate);
    const holesCount = lastRound?.holesCount ?? 18;
    const tourPlayers = data.playerRows
      .filter((r) => r.kind === 'existing')
      .map((r) => ({ id: (r as any).playerId }));

    const optimisticId = data.sendCreateRound(course, tourPlayers, nextDate, undefined, holesCount);
    if (optimisticId) {
      optimisticRoundIdRef.current = optimisticId;
      setSelection({ kind: 'round-settings', roundId: optimisticId });
      setExpandedRoundId(optimisticId);
      setMobileView('detail');
    }

    // Ensure courses are loaded for the settings editor
    if (data.courses.length === 0) {
      data.fetchCourses();
    }
  }, [data]);

  // --- Add course ---
  // Sends the user to the course editor and remembers which round asked, so the course
  // they create is selected for that round when golfCourse.changed comes back.
  const pendingCourseRoundIdRef = useRef<string | null>(null);

  const handleAddCourse = useCallback(
    (roundId: string) => {
      pendingCourseRoundIdRef.current = roundId;
      data.clearCreatedCourse();
      // Same entry point as the single-round picker: the round id lets the course editor
      // prefill from the round's current course instead of a stale globally-current round.
      router.push(`/config-course?roundId=${encodeURIComponent(roundId)}&closeOnSave=1`);
    },
    [data, router]
  );

  // The course editor clears the shared socket's subscription context (it needs the
  // unfiltered golfCourse.changed stream); take the tour subscription back on return.
  useEffect(() => {
    if (!isFocused) return;
    data.socketRef.current.setSubscriptionContext({ tourId });
  }, [isFocused, data.socketRef, tourId]);

  useEffect(() => {
    const created = data.createdCourse;
    const roundId = pendingCourseRoundIdRef.current;
    if (!created || !roundId) return;
    pendingCourseRoundIdRef.current = null;
    data.clearCreatedCourse();
    data.updateRoundSettings(roundId, { course: created });
  }, [data]);

  // When the optimistic round is replaced by the real one, update all references
  const optimisticRoundIdRef = useRef<string | null>(null);
  // Lets the safety effect below (which sees the stale selection in the same
  // commit) tell an optimistic-id swap apart from a genuine round deletion.
  const optimisticReplacedRef = useRef(false);
  useEffect(() => {
    const optimisticId = optimisticRoundIdRef.current;
    if (!optimisticId) return;
    const stillExists = data.orderedRounds.some((r) => r.id === optimisticId);
    if (!stillExists && data.orderedRounds.length > 0) {
      const realRound = data.orderedRounds[data.orderedRounds.length - 1];
      optimisticRoundIdRef.current = null;
      // Update selection if it pointed at the optimistic round
      if ('roundId' in selection && selection.roundId === optimisticId) {
        optimisticReplacedRef.current = true;
        setSelection({ kind: selection.kind as any, roundId: realRound.id });
        setExpandedRoundId(realRound.id);
      }
      // Update delete confirm dialog if it was targeting the optimistic round
      if (deleteRoundConfirm === optimisticId) {
        setDeleteRoundConfirm(realRound.id);
      }
    }
  }, [data.orderedRounds, selection, deleteRoundConfirm]);

  const handleDeleteRound = useCallback(
    (roundId: string) => {
      setDeleteRoundConfirm(roundId);
    },
    []
  );

  const handleConfirmDeleteRound = useCallback(() => {
    if (!deleteRoundConfirm) return;
    data.sendRoundRemove(deleteRoundConfirm);
    setDeleteRoundConfirm(null);
    // Navigation is handled by the safety effect when the round disappears from orderedRounds
  }, [deleteRoundConfirm, data]);

  // --- Step navigation ---
  const allSteps = useMemo((): SidebarSelection[] => {
    const steps: SidebarSelection[] = [
      { kind: 'tour-name' },
      { kind: 'tour-players' },
      { kind: 'tour-games' },
    ];
    data.orderedRounds.forEach((round) => {
      steps.push({ kind: 'round-settings', roundId: round.id });
      steps.push({ kind: 'round-players', roundId: round.id });
      steps.push({ kind: 'round-games', roundId: round.id });
    });
    return steps;
  }, [data.orderedRounds]);

  const currentStepIndex = useMemo(() => {
    return allSteps.findIndex((step) => {
      if (step.kind !== selection.kind) return false;
      if ('roundId' in step && 'roundId' in selection) return step.roundId === selection.roundId;
      return !('roundId' in step) && !('roundId' in selection);
    });
  }, [allSteps, selection]);

  const isLastStep = currentStepIndex >= allSteps.length - 1;

  // Safety: if selection references a round that no longer exists, navigate away
  useEffect(() => {
    if (!('roundId' in selection)) return;
    const exists = data.orderedRounds.some((r) => r.id === selection.roundId);
    if (!exists) {
      if (data.orderedRounds.length > 0) {
        const fallback = data.orderedRounds[data.orderedRounds.length - 1];
        setSelection({ kind: 'round-settings', roundId: fallback.id });
        setExpandedRoundId(fallback.id);
      } else {
        setSelection({ kind: 'tour-name' });
      }
      // The round may have been deleted by another editor while its detail view
      // was open on mobile; silently swapping to a different round would be
      // confusing, so return to the menu instead. An optimistic-id swap is not
      // a deletion — the user is mid-edit on the new round.
      if (isMobile && !optimisticReplacedRef.current) setMobileView('menu');
      optimisticReplacedRef.current = false;
    }
  }, [data.orderedRounds, selection, isMobile]);

  const handleNext = useCallback(() => {
    if (isLastStep) return;
    const nextStep = allSteps[currentStepIndex + 1];
    if (nextStep) {
      setSelection(nextStep);
      if ('roundId' in nextStep) {
        setExpandedRoundId(nextStep.roundId);
      }
    }
  }, [allSteps, currentStepIndex, isLastStep]);

  const handleSelectStep = useCallback((step: SidebarSelection) => {
    setSelection(step);
    if ('roundId' in step) {
      setExpandedRoundId(step.roundId);
    }
  }, []);

  const handleUpdateRoundSettings = useCallback(
    (roundId: string, updates: { name?: string; playDate?: string; courseName?: string; course?: Course; holesCount?: number }) => {
      data.updateRoundSettings(roundId, updates);
    },
    [data]
  );

  const handleGoToTours = useCallback(() => {
    router.push('/my-tours');
  }, [router]);

  // Take the user to where the round actually is: the result page when it is
  // finished, the last scored hole while playing, or the start page before play.
  const handleGoToRound = useCallback(
    (roundId: string) => {
      setCurrentTourId(tourId);
      setCurrentRoundId(roundId);
      const round = data.orderedRounds.find((r) => r.id === roundId);
      const status = (round?.status ?? '').toLowerCase();
      if (status === 'finished') {
        router.push(`/round/${roundId}/summary?tourId=${tourId}`);
        return;
      }
      if (status === 'draft' || status === '') {
        router.push(`/round/${roundId}/roundstart`);
        return;
      }
      const lastHole = data.lastScoredHoleByRoundId[roundId] || 1;
      setCurrentHole(lastHole);
      router.push(`/round/${roundId}/hole/${lastHole}/compact`);
    },
    [router, tourId, data.orderedRounds, data.lastScoredHoleByRoundId]
  );

  const deletingRound = useMemo(() => {
    if (!deleteRoundConfirm) return undefined;
    return data.orderedRounds.find((r) => r.id === deleteRoundConfirm);
  }, [deleteRoundConfirm, data.orderedRounds]);

  const deletingRoundName = deletingRound?.name || `Round`;
  // Rounds stay in "draft" until the first score is recorded, so any other
  // status means deleting also wipes recorded scores and results.
  const deletingRoundHasScores = !!deletingRound?.status && deletingRound.status !== 'draft';

  if (data.loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={appColors.accent.primary} />
        <Text style={styles.loadingText}>Loading tour...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <View style={[styles.breadcrumb, { paddingTop: insets.top + BREADCRUMB_PADDING_VERTICAL }]}>
        <View style={styles.breadcrumbTrail}>
          <Pressable
            onPress={() => router.back()}
            // The label is a single line of 13px text; hitSlop gives it a finger-sized target
            // without changing the row layout.
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            accessibilityRole="button"
            accessibilityLabel="Tours">
            <Text style={styles.breadcrumbLink}>← Tours</Text>
          </Pressable>
          <Text style={styles.breadcrumbSep}>/</Text>
          <Text style={styles.breadcrumbCurrent} numberOfLines={1}>
            {data.tourName || 'Tour'}
          </Text>
          <Text style={styles.breadcrumbSep}>/</Text>
          <Text style={styles.breadcrumbCurrent}>Edit</Text>
        </View>
        {/* This screen renders without the global header (headerShown: false), so the
            account menu lives here instead. */}
        <HeaderUser compact={isMobile} onOpenBugReport={requestBugReport} />
      </View>

      {/* Main content. Mobile is menu-first: the full-screen menu and the editor
          detail view (with its back header) take turns; desktop keeps the split. */}
      <View style={[styles.main, isMobile && styles.mainColumn]}>
        {isMobile && mobileView === 'menu' ? (
          <Sidebar
            variant="fullscreen"
            tourName={data.tourName}
            playerCount={playerCount}
            gameCount={gameCount}
            rounds={data.orderedRounds}
            selection={selection}
            expandedRoundId={expandedRoundId}
            onSelect={handleSelect}
            onExpandRound={handleExpandRound}
            onAddRound={handleAddRound}
            onDeleteRound={handleDeleteRound}
            onGoToRound={handleGoToRound}
          />
        ) : null}
        {!isMobile && (
          <View style={[styles.sidebar, { width: sidebarWidth }]}>
            <Sidebar
              tourName={data.tourName}
              playerCount={playerCount}
              gameCount={gameCount}
              rounds={data.orderedRounds}
              selection={selection}
              expandedRoundId={expandedRoundId}
              onSelect={handleSelect}
              onExpandRound={handleExpandRound}
              onAddRound={handleAddRound}
              onDeleteRound={handleDeleteRound}
              onGoToRound={handleGoToRound}
            />
          </View>
        )}
        {isMobile && mobileView === 'detail' && (
          <MobileDetailHeader
            title={sectionTitle(selection, data.orderedRounds)}
            onBack={handleBackToMenu}
          />
        )}
        {(!isMobile || mobileView === 'detail') && (
        <View style={styles.editorPanel}>
          <EditorPanel
            selection={selection}
            allSteps={allSteps}
            tourId={tourId}
            tourName={data.tourName}
            onTourNameChange={data.setTourName}
            onSendTourUpdate={data.sendTourUpdate}
            saving={data.saving}
            error={data.error}
            statusNote={data.statusNote}
            tourImageKey={data.tourImageKey}
            tourImagePreviewUri={data.imagePreviewUri}
            imageUploadPhase={data.imageUploadPhase}
            onUploadTourImage={data.uploadTourImage}
            playerRows={data.playerRows}
            availablePlayers={data.availablePlayers}
            playerRowOpen={data.playerRowOpen}
            onSetPlayerRows={data.setPlayerRows}
            onSetPlayerRowOpen={data.setPlayerRowOpen}
            onSendTourMemberAdd={data.sendTourMemberAdd}
            onSendTourMemberRemove={data.sendTourMemberRemove}
            onSendTemporaryPlayerCreate={data.sendTemporaryPlayerCreate}
            enabledGameDefinitions={data.enabledGameDefinitions}
            tournamentGameIds={data.tournamentGamesState.gameIds}
            tournamentWeightsByGameId={data.tournamentGamesState.weightsByGameId}
            tournamentRuleSetByGameId={data.tournamentGamesState.ruleSetByGameId}
            onUpdateTournamentGames={data.updateTournamentGames}
            onUpdateTournamentGameWeight={data.updateTournamentGameWeight}
            onUpdateTournamentGameRuleSet={data.updateTournamentGameRuleSet}
            rounds={data.orderedRounds}
            courses={data.courses}
            recentCourses={recentCourses}
            loadingCourses={data.loadingCourses}
            courseError={data.courseError}
            onAddCourse={handleAddCourse}
            tourMemberOptions={data.tourMemberOptions}
            roundPlayerIdsByRoundId={data.roundPlayerIdsByRoundId}
            playerIdsWithScoresByRoundId={data.playerIdsWithScoresByRoundId}
            playerNameById={data.playerNameById}
            onSendRoundMemberAdd={data.sendRoundMemberAdd}
            onSendRoundMemberRemove={data.sendRoundMemberRemove}
            onSendRoundGroupsSwap={data.sendRoundGroupsSwap}
            roundGroupsByRoundId={data.roundGroupsByRoundId}
            onUpdateRoundSettings={handleUpdateRoundSettings}
            roundGamesByRoundId={data.roundGamesByRoundId}
            onUpdateRoundGames={data.updateRoundGames}
            onUpdateRoundGameWeight={data.updateRoundGameWeight}
            onUpdateRoundGameRuleSet={data.updateRoundGameRuleSet}
            isLastStep={isLastStep}
            onNext={handleNext}
            onGoToTours={handleGoToTours}
            onAddRound={handleAddRound}
            onSelectStep={handleSelectStep}
            isMobile={isMobile}
          />
        </View>
        )}
      </View>

      <ConfirmDialog
        visible={deleteRoundConfirm !== null}
        title="Delete Round"
        message={
          deletingRoundHasScores
            ? `"${deletingRoundName}" has recorded scores. Deleting it will permanently remove all its scores and results. This cannot be undone.`
            : `Are you sure you want to delete "${deletingRoundName}"? This cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteRound}
        onCancel={() => setDeleteRoundConfirm(null)}
      />
    </View>
  );
}

const BREADCRUMB_PADDING_VERTICAL = 10;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    backgroundColor: appColors.background.app,
  },
  loadingText: {
    fontSize: 14,
    color: appColors.text.muted,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    // paddingTop is applied inline so it can carry the status-bar inset.
    paddingVertical: BREADCRUMB_PADDING_VERTICAL,
    backgroundColor: appColors.surface.base,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
    // The account dropdown is absolutely positioned inside this row; without a
    // stacking context the main content below paints over it.
    zIndex: 20,
  },
  breadcrumbTrail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  breadcrumbLink: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.accent.primary,
  },
  breadcrumbSep: {
    fontSize: 13,
    color: appColors.text.muted,
  },
  breadcrumbCurrent: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.text.secondary,
    maxWidth: 200,
  },
  main: {
    flex: 1,
    flexDirection: 'row',
  },
  mainColumn: {
    flexDirection: 'column',
  },
  sidebar: {
    flexShrink: 0,
  },
  editorPanel: {
    flex: 1,
  },
});
